import React from "react";
import Image from "next/image";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import { format } from "date-fns";
import { FaSpinner } from "react-icons/fa";
import { Frown, CheckCircle } from "lucide-react";
import { convertLexicalToHTML } from "@payloadcms/richtext-lexical/html";

const ArticlePage = ({ article, config }) => {
  const router = useRouter();

  if (router.isFallback) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">
        <FaSpinner className="animate-spin text-4xl mb-4" />
        <div className="animate-pulse text-2xl">Loading...</div>
      </div>
    );
  }

  if (!article || !article?.title) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-black text-white">
        <Frown className="h-12 w-12 mb-4" />
        <div className="text-2xl">Article not found</div>
        <Link href="/support" className="mt-4 text-blue-500 hover:underline">
          Go back to Support
        </Link>
      </div>
    );
  }

  const renderContent = (content) => {
    if (typeof content === "string") {
      content = (content.replace(/src="\/api\//g, `src="https://assets.combatwarriors.gg/payload/`)).replace(/\/?file\//gmi, '/');
    }

    content = content.replace(
      /<img\s+([^>]*?)>/g,
      (match, attrs) => {
        return `<img class="m-auto rounded-lg" ${attrs}>`;
      }
    );

    return content;
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Head>
        <title>{article.title} | Combat Warriors</title>
        <meta
          name="description"
          content={article?.shortDescription || "Support article"}
        />
        <meta property="og:title" content={article.title} />
        <meta
          property="og:description"
          content={article?.shortDescription || "Support article"}
        />
        <meta
          property="og:image"
          content={article?.image || "/default-image.jpg"}
        />
        <meta
          property="og:url"
          content={`https://combatwarriors.com/support/article/${article.slug}`}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="keywords"
          content={`combat warriors, combat warriors support, ${article.title}, ${article.keywords.join(", ")}`}
        />
      </Head>

      <div className="relative pt-24 pb-2">
        <div
          className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:24px_24px] w-screen"
          style={{ zIndex: 1 }}
        ></div>

        {/* Radial gradient mask for the grid */}
        <div
          className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_25%,#000_100%)]"
          style={{ zIndex: 2 }}
        ></div>

        {/* Glowing accent */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-white/5 blur-[80px]"
          style={{ zIndex: 3 }}
        ></div>

        <div className="relative z-10 max-w-4xl m-auto mx-12 lg:mx-auto">
          {/* Breadcrumb */}
          <div className="mb-8 flex items-center text-sm">
            <Link
              href="/support"
              className="text-white/50 hover:text-white transition-colors"
            >
              Support
            </Link>
            <span className="mx-2 text-white/50 cursor-default">/</span>
            <span className="text-white/50 cursor-default">
              {article.category.name}
            </span>
            <span className="mx-2 text-white/50 cursor-default">/</span>
            <span className="text-white cursor-default">{article.title}</span>
          </div>

          {/* Article Header */}
          <div className="border-b border-white/50 pb-8 mb-8">
            <h1 className="text-4xl font-bold mb-4">{article.title}</h1>
            <p className="text-xl text-gray-300">{article.shortDescription}</p>
          </div>

          {/* Article Content */}
          <div
            className="prose prose-invert max-w-none"
            dangerouslySetInnerHTML={{ __html: renderContent(article.content) }}
          />

          <hr className="my-12 border-white/20" />

          {/* Ticket CTA */}
          <div className="relative mt-16 mb-12 p-8 rounded-lg text-center overflow-hidden border border-white/10 bg-black max-w-4xl mx-auto">
            <div className="absolute inset-0">
              <Image
                src="/support_banner.png"
                alt="Support Background"
                layout="fill"
                objectFit="cover"
                className="opacity-25"
              />
            </div>

            <div className="relative z-10">
              <h2 className="text-2xl font-bold mb-4">Need more help?</h2>
              <p className="mb-6 text-gray-300 mx-auto">
                Haven't found the answer you're looking for? Our support team is
                here to help!
              </p>
              <Link
                href="/support/ticket"
                className="inline-flex items-center justify-center bg-white text-black px-6 py-2 text-sm rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                <CheckCircle className="mr-2 h-5 w-5" />
                Open a Ticket
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export async function getStaticPaths() {
  try {
    const articles = await fetch(`${process.env.CMS_URL}/api/articles`)
      .then((res) => res.json())
      .then((data) =>
        data?.docs?.length
          ? data.docs.map((article) => ({ params: { slug: article.id } }))
          : []
      )
      .catch((err) => {
        console.error("Error fetching articles:", err);
        return [];
      });

    return {
      paths: articles,
      fallback: true,
    };
  } catch (error) {
    console.error("Error fetching articles:", error);
    return {
      paths: [],
      fallback: true,
    };
  }
}

const allVariations = (keywords) => {
  return [...new Set(keywords.reduce((acc, keyword) => {
    const toAdd = [];
    
    if (keyword.includes(" ")) {
      const words = keyword.split(" ");
      const joined = words.join("").toLowerCase();
      const hyphenated = words.join("-").toLowerCase();
      const initials = words.map((word) => word[0].toLowerCase()).join("");
      toAdd.push(joined, hyphenated, initials);
    }
    
    acc.push(keyword.toLowerCase(), ...toAdd);
    return acc;
  }, []))].sort((a, b) => a.localeCompare(b));
}

export async function getStaticProps({ params }) {
  try {
    const article = await fetch(
      `${process.env.CMS_URL}/api/articles/${params.slug}?depth=1`
    )
      .then((res) => res.json())
      .then((data) => {
        if (!data) {
          return null;
        }
        return {
          ...data,
          content: data.content || {},
        };
      })
      .catch((err) => {
        console.error("Error fetching article:", err);
        return null;
      });

    delete article?.status;
    delete article?.createdAt;
    delete article?.updatedAt;
    delete article?.author;

    delete article?.category?.createdAt;
    delete article?.category?.updatedAt;
    delete article?.category?.id;

    const newKeywords = [];
    for (const key in article?.keywords) {
      newKeywords.push(article?.keywords[key].keyword);
    }

    return {
      props: {
        article: article
          ? {
              ...article,
              content: article.content
                ? convertLexicalToHTML({ data: article.content })
                : "",
              keywords: allVariations(newKeywords),
            }
          : null,
        config: {
          cmsUrl: process.env.CMS_URL,
        },
      },
      revalidate: 60, // Revalidate every minute
    };
  } catch (error) {
    console.error("Error fetching article:", error);
    return {
      notFound: true,
    };
  }
}

export default ArticlePage;
